import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT = 20000;

function mapsKey() {
  if (typeof document === "undefined" || !document.documentElement) {
    return "";
  }

  return document.documentElement.dataset.mapsGeocoderKey || "";
}

function isFiniteNumber(v) {
  return typeof v === "number" && isFinite(v);
}

function validLat(v) {
  return isFiniteNumber(v) && v >= -90 && v <= 90;
}

function validLon(v) {
  return isFiniteNumber(v) && v >= -180 && v <= 180;
}

function str(v) {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
  }

  cancelAutocomplete() {
    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.zip) {
      s = g.zip;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state && !g.street) {
      p.push(g.state);
    }

    if (g.country && !g.street) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  // Legacy-Fallback für Antworten ohne properties.type
  _legacyType(props) {
    if (props.osm_key == "highway") {
      return "street";
    }
    if (props.osm_key == "boundary") {
      return "district";
    }
    if (props.osm_key == "place") {
      if (props.osm_value == "suburb" || props.osm_value == "quarter" || props.osm_value == "neighbourhood") {
        return "district";
      }
      if (props.osm_value == "city" || props.osm_value == "town" || props.osm_value == "village" || props.osm_value == "municipality") {
        return "city";
      }
    }
    if (props.housenumber) {
      return "house";
    }

    return null;
  }

  _geoaddress(l) {
    if (!l || typeof l !== "object" || !l.geometry || !Array.isArray(l.geometry.coordinates)) {
      return null;
    }

    // Koordinaten sind [lng, lat]
    const lng = l.geometry.coordinates[0];
    const lat = l.geometry.coordinates[1];
    if (!validLat(lat) || !validLon(lng)) {
      return null;
    }

    const props = l.properties && typeof l.properties === "object" ? l.properties : {};
    const type = str(props.type) || this._legacyType(props);

    const name = str(props.name);
    const street = str(props.street);
    const housenumber = str(props.housenumber);
    const postcode = str(props.postcode);
    const city = str(props.city);
    const district = str(props.district);

    const geoaddress = {};

    switch (type) {
      case "city":
        geoaddress.city = city || name;
        break;

      case "district":
        geoaddress.city = city;
        geoaddress.district = district || name;
        break;

      case "street":
        geoaddress.street = street || name;
        geoaddress.city = city;
        geoaddress.district = district;
        break;

      case "house":
        geoaddress.street = street;
        geoaddress.number = street ? housenumber : null;
        geoaddress.city = city;
        geoaddress.district = district;
        break;

      default:
        // locality, county, state, country und unbekannte Typen
        geoaddress.street = street;
        geoaddress.number = street ? housenumber : null;
        geoaddress.city = city;
        geoaddress.district = district;
    }

    if (geoaddress.city && geoaddress.district && geoaddress.city == geoaddress.district) {
      geoaddress.district = null;
    }

    geoaddress.zip = postcode;
    geoaddress.lat = lat;
    geoaddress.lng = lng;
    geoaddress.state = str(props.state);
    geoaddress.country = str(props.country);

    Object.keys(geoaddress).forEach(k => {
      if (geoaddress[k] === null || geoaddress[k] === undefined) {
        delete geoaddress[k];
      }
    });

    let text = this._geoaddressText(geoaddress);

    // Ohne Straße/Ort den Feature-Namen als Beschriftung erhalten
    if (name && !geoaddress.street && !geoaddress.city && !geoaddress.district) {
      text = text ? name + ", " + text : name;
    } else if (name && type == "house" && !geoaddress.street) {
      text = text ? name + ", " + text : name;
    }

    if (!text) {
      return null;
    }

    geoaddress.text = text;

    return geoaddress;
  }

  _features(data) {
    const results = [];

    if (data && Array.isArray(data.features)) {
      data.features.forEach(l => {
        const geoaddress = this._geoaddress(l);
        if (geoaddress) {
          results.push(geoaddress);
        }
      });
    }

    return results;
  }

  // Liefert immer ein Ergebnis: Treffer, [] bei Abbruch/Fehler/fehlendem Key
  autocomplete(q) {
    this.cancelAutocomplete();

    const key = mapsKey();
    if (!key || !q || !String(q).trim()) {
      return Promise.resolve([]);
    }

    const request = {
      source: this.axios.CancelToken.source()
    };
    this.axiosRequest = request;

    const params = new URLSearchParams();
    params.append("q", q);
    params.append("lang", "de");
    params.append("limit", 10);
    params.append("key", key);

    return this.axios.get(SEARCH_URL, {
      cancelToken: request.source.token,
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      return this._features(res.data);
    }).catch(thrown => {
      if (!this.axios.isCancel(thrown)) {
        console.warn("Geocoding fehlgeschlagen", thrown && thrown.response ? thrown.response.status : (thrown && thrown.code) || "");
      }
      return [];
    }).finally(() => {
      if (this.axiosRequest === request) {
        this.axiosRequest = null;
      }
    });
  }

  reverse(lat, lng) {
    lat = Number(lat);
    lng = Number(lng);

    if (!validLat(lat) || !validLon(lng)) {
      return Promise.reject(new Error("invalid coordinates"));
    }

    const key = mapsKey();
    if (!key) {
      return Promise.reject(new Error("missing maps key"));
    }

    const params = new URLSearchParams();
    params.append("lat", lat);
    params.append("lon", lng);
    params.append("lang", "de");
    params.append("key", key);

    return this.axios.get(REVERSE_URL, {
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      const results = this._features(res.data);
      if (results.length > 0) {
        return results[0];
      }

      throw new Error("invalid data");
    }, () => {
      throw new Error("reverse geocoding failed");
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
